import React, {useState} from 'react';
import {useMediaQuery} from "react-responsive";
import { GiSave, GiCancel } from "react-icons/gi";
import { BsPencilSquare } from "react-icons/bs";
import { Bounce, toast } from "react-toastify";

import { DisplayNameRequest } from "../goveepb/internal/proto/govee_pb"
import ExpandedDeviceChartDialog from "./ExpandedDeviceChartDialog";
import BatteryMeter from "./BatteryMeter";
import useGrpcWithAuth from "../hooks/useGrpcWithAuth";

import WifiIndicator, {
  DBMToSignalStrength,
  SignalStrength
} from 'react-wifi-indicator';
import moment from "moment/moment";

const signalStrengthOffset = 15;

const DeviceHeaderLabels = ({client, hasData, lastItem, device, onLegendClick}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(device.getDisplayName());
  const callWithRetry = useGrpcWithAuth();

  const onLabelClick = (e) => {
    setIsEditing(!isEditing);
    setDisplayName(device.getDisplayName());
  };

  const updateDisplayName = () => {
    const request = new DisplayNameRequest();
    request.setDeviceId(device.getId());
    request.setDisplayName(displayName);
    console.log("Updating displayName of device", device.getId(), "to", displayName);
    callWithRetry(
      (metadata, cb) => client.updateDeviceDisplayName(request, metadata, cb),
      (err, response) => {
        if (err) {
          console.error("Device update failed", err, response);
          toast.error('An error occurred updating display name: "' + err.message + '"', {
            position: "bottom-left",
            autoClose: 4000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
            transition: Bounce,
          });
        } else {
          setDisplayName(displayName)
          device.setDisplayName(displayName);
          toast.info('Display name updated', {
            position: "bottom-left",
            autoClose: 4000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
            transition: Bounce,
          });
        }
        setIsEditing(!isEditing);
      }
    );
  }

  const handleSubmit = (e) => {
    // prevents the default browser behavior of submitting the form and refreshing the page.
    e.preventDefault();

    updateDisplayName();
  };

  let deviceDisplayName;
  if (isEditing) {
    deviceDisplayName =
        <div className="device-header-labels-display-name">
          <input type="text"
                 value={displayName}
                 onChange={(e) => setDisplayName(e.target.value)}
                 maxLength={100}
                 placeholder="Name"/>
          <GiSave className="device-header-labels-display-name-form" title="Update device display name" size="18px" onClick={handleSubmit}/>
          <GiCancel className="device-header-labels-display-name-form" title="Cancel" size="18px" onClick={onLabelClick}/>
        </div>
  } else {
    deviceDisplayName =
        <h3 className="device-header-labels-name"
            title={device.getId()}>
          <a onClick={onLabelClick}>{device.getDisplayName()}</a>
          <BsPencilSquare className="device-header-labels-display-name-edit" title="Edit device display name" size="14px" onClick={onLabelClick}/>
      </h3>
  }

  return (
      <div className="device-header-labels-container">
        <div className="device-header-labels">
          {deviceDisplayName}
          {hasData &&
              <>
                <a onClick={onLegendClick} name="temperature-legend" className="device-header-labels-byline device-header-labels-temperature">
                  Temp: {(Math.round(lastItem.getTemperature() * 10)
                    / 10).toFixed(1)}°F,
                </a>
                <a onClick={onLegendClick} name="humidity-legend" className="device-header-labels-byline device-header-labels-humidity">
                  Humid: {(Math.round(lastItem.getHumidity() * 10)
                    / 10).toFixed(1)}%
                </a>
                {lastItem.getPressure() > 0 &&
                <a name="battery-legend" className="device-header-labels-byline device-header-labels-battery">
                  Pressure: {(Math.round(lastItem.getPressure() * 10)
                    / 10).toFixed(1)} mmHg
                </a>
                }
              </>
          }
        </div>
      </div>
  );
};

const DeviceHeaderBluetoothSignalIndicator = ({client, device, showSignalLabel, showExpand, onDragZoom}) => {
  const isDesktopOrLaptop = useMediaQuery({
    query: '(min-width: 1124px)'
  });

  const hasData = device && device.getSignalsList().length > 0;
  if (!hasData) {
    return (
        <div className="device-wifi-indicator-container"/>
    )
  }

  const strength =
      DBMToSignalStrength(device.getBestSignalStrength() + signalStrengthOffset)
      || SignalStrength.DISCONNECTED

  const shouldShowExpand = isDesktopOrLaptop && showExpand;

  const sortedSignals = device.getSignalsList().slice(0).sort(
      (a, b) => b.getLastUpdated() - a.getLastUpdated());
  const lastUpdatedMillis = sortedSignals[0].getLastUpdated();

  // Assemble signal alt title text
  const now = Date.now();
  var signalTitles = [];
  const formatStr = sortedSignals.some((s) => now-s.getLastUpdated() > 1000*3600) ? 'MMM Do h:mm a' : 'h:mm a';
  sortedSignals.forEach((s) => {
    signalTitles.push(moment(s.getLastUpdated()).format(formatStr) + " - " + s.getClientHostname() + ": " + s.getSignalStrength() + " dBm");
  });
  const signalTitle = signalTitles.join("\n");

  return (
      <div className="device-wifi-indicator-container">
        {showSignalLabel && <div className="device-wifi-indicator-label"
                                 title={signalTitle}>Signal: {strength}</div>}
        <WifiIndicator title={strength} strength={strength} style={{
          "margin": "0 5px 0 5px",
          "filter": "hue-rotate(20deg)",
          "height": 16,
          "float": "right",
        }}/>
        <div title={signalTitle} className="device-wifi-indicator-time">
          {moment(lastUpdatedMillis).format('h:mm a')}
        </div>
        {shouldShowExpand && <ExpandedDeviceChartDialog key={device.getId()} client={client} device={device} onDragZoom={onDragZoom}/>}
      </div>
  )
}

const DeviceChartHeader = ({client, device, data, showSignalLabel, showExpand, hideBattery, onDragZoom, onLegendClick}) => {
  const hasData = data.length > 0;
  const lastItem = (hasData && data[data.length - 1]) || {};

  return (
      <div className="device-header">
        <DeviceHeaderLabels client={client} hasData={hasData} lastItem={lastItem} device={device} onLegendClick={onLegendClick}/>
        {!hideBattery &&
            <BatteryMeter battery={device.getBattery()} numLevels="5" onLegendClick={onLegendClick}/>
        }
        <DeviceHeaderBluetoothSignalIndicator client={client} device={device} showSignalLabel={showSignalLabel} showExpand={showExpand} onDragZoom={onDragZoom}/>
      </div>
  );

};

export default DeviceChartHeader
